/**
 * Form Validation Service
 * Single responsibility: Handle all form validation logic
 */

import type { ParsedField } from '../utils/templateParsers';

export interface FormFieldData {
  value: string;
  hasError: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  updatedFields: ParsedField[];
  errorMessage?: string;
}

/**
 * Validate required fields client-side
 */
export const validateRequiredFields = (
  fields: ParsedField[],
  formData: Record<string, FormFieldData>
): ValidationResult => {
  const requiredKeys = fields
    .filter((f) => f.required)
    .map((f) => f.attributeName)
    .filter(Boolean);

  const updatedFields = fields.map((field) => {
    const attr = field.attributeName || '';
    const isRequired = requiredKeys.includes(attr);
    const fieldData = formData[attr];
    const value = fieldData.value || '';
    const empty = value.trim() === '';

    const getErrorMessage = () => {
      if (!isRequired || !empty) {
        return '';
      }

      if (attr === 'endpoints.ownerkey') {
        return 'Please select a user group';
      }

      return 'This field is required';
    };

    return {
      ...field,
      hasError: isRequired && empty,
      errorMessage: getErrorMessage(),
    };
  });

  const anyError = updatedFields.some((f) => f.hasError);

  return {
    isValid: !anyError,
    updatedFields,
  };
};

/**
 * Handle application name validation error
 */
export const handleApplicationNameError = (
  fields: ParsedField[]
): ParsedField[] =>
  fields.map((field) =>
    field.attributeName === 'securitysystems.systemname'
      ? {
          ...field,
          hasError: true,
          errorMessage: 'Application name already exists',
        }
      : field
  );

/**
 * Handle UserGroup validation error
 */
export const handleUserGroupError = (fields: ParsedField[]): ParsedField[] =>
  fields.map((field) =>
    field.attributeName === 'endpoints.ownerkey'
      ? {
          ...field,
          hasError: true,
          errorMessage: 'Please select a user group',
        }
      : field
  );
